import { sectionRenderer } from './section-renderer.js';
import { Component } from './component.js';
import { FilterUpdateEvent, ThemeEvents } from './events.js';
import { debounce, formatMoney, startViewTransition } from './utilities.js';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));


    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    //code commented to speedup the filter applied in desktop

    // const viewTransition = !this.closest('dialog');

    // if (viewTransition) {
    //   startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    // } else {
    //   sectionRenderer.renderSection(this.sectionId);
    // }
    sectionRenderer.renderSection(this.sectionId);
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  // updateFilters() {
  //   const facetsForm = this.closest('facets-form-component');
  //   facetsForm?.querySelectorAll('input').forEach(input => {
  //     let clickedValue = input.closest('.facets__inputs-list-item')?.querySelector('.checkbox__label-text')?.innerHTML.trim().toLowerCase().replace(/\s*\([a-z]+\)\s*/gi, '');
  //     if(input.checked) {
  //       document.querySelectorAll('.quickFilter__text').forEach((each)=>{
  //         let pillValueText =each.innerHTML.trim().toLowerCase().replace(/\s*\([a-z]+\)\s*/gi, '');
  //         if(clickedValue === pillValueText) {
  //           each.closest('quick-filter-pill')?.classList.add('active');
  //         }})
  //     }else{ 
  //       document.querySelectorAll('.quickFilter__text').forEach((each)=>{
  //         let pillValueText =each.innerHTML.trim().toLowerCase().replace(/\s*\([a-z]+\)\s*/gi, '');
  //         if(clickedValue === pillValueText) {
  //           each.closest('quick-filter-pill')?.classList.remove('active');
  //         }})
  //     }
  //   });

  //   if (!(facetsForm instanceof FacetsFormComponent)) return;

  //   facetsForm.updateFilters();
  //   this.#updateSelectedFacetSummary();
  // }

  updateFilters() {
  const facetsForm = this.closest('facets-form-component');
  if (!facetsForm) return;

  const normalize = (text = '') =>
    text.trim().toLowerCase().replace(/\s*\([a-z]+\)\s*/gi, '');

  const pills = Array.from(
    document.querySelectorAll('.quickFilter__text')
  ).map(el => ({
    value: normalize(el.innerHTML),
    pill: el.closest('quick-filter-pill')
  }));

  facetsForm.querySelectorAll('input').forEach(input => {
    const clickedValue = normalize(
      input
        .closest('.facets__inputs-list-item')
        ?.querySelector('.checkbox__label-text')
        ?.innerHTML
    );

    if (!clickedValue) return;

    pills.forEach(({ value, pill }) => {
      if (value === clickedValue) {
        pill?.classList.toggle('active', input.checked);
      }
    });
  });

  if (!(facetsForm instanceof FacetsFormComponent)) return;

  facetsForm.updateFilters();
  this.#updateSelectedFacetSummary();
}

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  // Initialize immediately if DOM is ready, otherwise delay
    if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
      this.#initializeSlider();
    } else {
      // Use MutationObserver to detect when elements are added
      this.#observeForElements();
    }

    // Listen for filter updates to preserve slider state
    this.addEventListener('filter-updated', this.#onFilterUpdated.bind(this));

    // Listen for re-initialization events
    this.addEventListener('reinitialize-slider', this.#onReinitializeSlider.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#removeSliderListeners();

    // Clean up observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    // Prevent duplicate calls
    if (this._isUpdatingFilters) {
      return;
    }
    
    // Safety check - ensure refs exist
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) {
      console.warn('[Price Slider] updatePriceFilterAndResults: minInput or maxInput not found');
      return;
    }
    
    this._isUpdatingFilters = true;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) {
      console.warn('[Price Slider] FacetsFormComponent not found');
      this._isUpdatingFilters = false;
      return;
    }

    try {
      facetsForm.updateFilters();
      this.#setMinAndMaxValues();
      this.#updateSummary();
    } catch (error) {
      console.error('[Price Slider] Error in updatePriceFilterAndResults:', error);
      // Reset flag on error
      this._isUpdatingFilters = false;
      throw error; // Re-throw to prevent silent failures
    }
    
    // Reset flag after a delay to allow for filter updates
    setTimeout(() => {
      this._isUpdatingFilters = false;
    }, 100);
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    // Safety check - ensure input exists
    if (!input) {
      console.warn('[Price Slider] adjustToValidValues: input not found');
      return;
    }

    if (!input.value || input.value.trim() === '') return;

    const value = Number(input.value.replace(/,/g, '').trim());
    
    // Safety check - ensure value is valid
    if (isNaN(value)) {
      console.warn('[Price Slider] adjustToValidValues: invalid value', input.value);
      return;
    }
    
    // Get max from the most reliable source: maxThumb's data-range-max
    // This is the definitive maximum range for the slider
    let max = this.maxPrice || 1000;
    if (this.maxThumb && this.maxThumb.dataset?.rangeMax) {
      const rangeMaxAttr = this.maxThumb.dataset.rangeMax.replace(/,/g, '');
      const parsedMax = Number(rangeMaxAttr);
      if (!isNaN(parsedMax)) {
        max = parsedMax;
      }
    } else {
      // Fallback to data-max attribute
      const maxAttr = input.getAttribute('data-max') ?? '';
      if (maxAttr) {
        const parsedMax = Number(maxAttr.replace(/,/g, ''));
        if (!isNaN(parsedMax)) {
          max = parsedMax;
        }
      }
    }
    
    // Get min from data-min attribute or default to 0
    const minAttr = input.getAttribute('data-min') ?? '';
    const min = minAttr ? Number(minAttr.replace(/,/g, '')) : 0;

    if (value < min) {
      input.value = min.toString();
    } else if (value > max) {
      input.value = max.toString();
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;
    
    // Safety check - ensure refs exist
    if (!minInput || !maxInput) {
      console.warn('[Price Slider] setMinAndMaxValues: minInput or maxInput not found');
      return;
    }

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }

  
  /**
   * Initialize the price slider
   */
  #initializeSlider() {
    const { sliderTrack, minThumb, maxThumb, sliderRange, minLabel, maxLabel } = this.refs;
    const { minInput, maxInput } = this.refs;

    if (!sliderTrack || !minThumb || !maxThumb || !sliderRange) return;

    // Check if slider is already initialized to prevent re-initialization
    if (this.sliderInitialized) {
      // Re-initialize for AJAX updates
      this.#reinitializeSlider();
      return;
    }

    this.sliderTrack = sliderTrack;
    this.minThumb = minThumb;
    this.maxThumb = maxThumb;
    this.sliderRange = sliderRange;
    this.minLabel = minLabel;
    this.maxLabel = maxLabel;
    this.minInput = minInput;
    this.maxInput = maxInput;

    // Get price range - use the actual filter range_max
    this.minPrice = 0;

    // Get the actual range_max from multiple sources
    const rangeMaxElement = this.sliderTrack.closest('.price-facet').querySelector('[data-range-max]');
    const rangeMax = rangeMaxElement ? Number(rangeMaxElement.getAttribute('data-range-max')) : null;

    // Get range_max from maxThumb data-range-max attribute if available
    // This is the definitive source - it contains filter.range_max (e.g., 2999)
    const maxThumbRangeMaxRaw = this.maxThumb.getAttribute('data-range-max');
    const maxThumbRangeMax = maxThumbRangeMaxRaw 
      ? Number(maxThumbRangeMaxRaw.replace(/,/g, '').trim()) 
      : null;

    // Get values from various sources for debugging
    const dataMaxAttrRaw = this.maxInput.getAttribute('data-max');
    const dataMaxAttr = dataMaxAttrRaw ? Number(dataMaxAttrRaw.replace(/,/g, '').trim()) : null;
    const minThumbDataValue = this.minThumb.dataset.value ? Number(this.minThumb.dataset.value.replace(/,/g, '')) : null;
    const maxThumbDataValue = this.maxThumb.dataset.value ? Number(this.maxThumb.dataset.value.replace(/,/g, '')) : null;

    // Prioritize maxThumbRangeMax FIRST - this is the definitive source (filter.range_max = 2999)
    // Then fall back to other sources
    // maxThumbRangeMax should always be used as it comes directly from filter.range_max
    this.maxPrice = maxThumbRangeMax || rangeMax || dataMaxAttr || 1000;

    this.range = this.maxPrice - this.minPrice;

    // Read initial values from inputs if they exist, otherwise use Liquid-set values
    // Clean the input values by removing currency symbols and formatting
    const inputMinRaw = this.minInput.value ? this.minInput.value.replace(/[₹,\s]/g, '').trim() : '';
    const inputMaxRaw = this.maxInput.value ? this.maxInput.value.replace(/[₹,\s]/g, '').trim() : '';
    const inputMin = inputMinRaw ? Number(inputMinRaw) : null;
    const inputMax = inputMaxRaw ? Number(inputMaxRaw) : null;
    
    // Don't use maxPrice as fallback for currentMax - use actual current filter value or 0
    // maxPrice is the maximum possible range, not the current filter value
    // Check for NaN explicitly - if input is NaN, treat it as null
    const currentMin = (inputMin !== null && !isNaN(inputMin)) ? inputMin : (minThumbDataValue !== null ? minThumbDataValue : 0);
    const currentMax = (inputMax !== null && !isNaN(inputMax)) ? inputMax : (maxThumbDataValue !== null ? maxThumbDataValue : 0);

    // Set thumb positions based on current values
    this.#setThumbValue('min', currentMin);
    this.#setThumbValue('max', currentMax);

    // Store the current values for reference
    this.currentMinValue = currentMin;
    this.currentMaxValue = currentMax;

    // Important: don't auto-populate inputs at default bounds (min=0, max=range_max),
    // otherwise the price facet gets applied whenever any other filter triggers a refresh.
    this.#syncPriceInputsForSubmission();

    // Update labels
    this.#updateSliderLabels();

    // Add event listeners
    this.#addSliderListeners();

    // Validate constraints after initialization
    this.#validateSliderConstraints();

    // Mark as initialized
    this.sliderInitialized = true;
  }

  /**
   * Re-initialize slider for AJAX updates
   */
  #reinitializeSlider() {
    const { sliderTrack, minThumb, maxThumb, sliderRange, minLabel, maxLabel } = this.refs;
    const { minInput, maxInput } = this.refs;

    if (!sliderTrack || !minThumb || !maxThumb || !sliderRange) return;

    // Update references
    this.sliderTrack = sliderTrack;
    this.minThumb = minThumb;
    this.maxThumb = maxThumb;
    this.sliderRange = sliderRange;
    this.minLabel = minLabel;
    this.maxLabel = maxLabel;
    this.minInput = minInput;
    this.maxInput = maxInput;

    // Get the actual range_max from multiple sources
    const rangeMaxElement = this.sliderTrack.closest('.price-facet').querySelector('[data-range-max]');
    const rangeMax = rangeMaxElement ? Number(rangeMaxElement.getAttribute('data-range-max')) : null;

    // Get range_max from maxThumb data-range-max attribute if available
    // This is the definitive source - it contains filter.range_max (e.g., 2999)
    const maxThumbRangeMaxRaw = this.maxThumb.getAttribute('data-range-max');
    const maxThumbRangeMax = maxThumbRangeMaxRaw 
      ? Number(maxThumbRangeMaxRaw.replace(/,/g, '').trim()) 
      : null;

    // Prioritize maxThumbRangeMax FIRST - this is the definitive source (filter.range_max = 2999)
    // maxThumbRangeMax should always be used as it comes directly from filter.range_max
    this.maxPrice = maxThumbRangeMax || rangeMax || this.maxPrice || 1000;

    this.range = this.maxPrice - this.minPrice;

    // Read current values from Liquid-set positions
    // Don't use maxPrice as fallback - use actual current filter value or 0
    const currentMin = Number(this.minThumb.dataset.value) || 0;
    const currentMax = Number(this.maxThumb.dataset.value) || 0;

    // Update stored values
    this.currentMinValue = currentMin;
    this.currentMaxValue = currentMax;

    // Update labels
    this.#updateSliderLabels();

    // Re-add event listeners
    this.#removeSliderListeners();
    this.#addSliderListeners();
  }

  /**
   * Add slider event listeners
   */
  #addSliderListeners() {
    this.minThumb.addEventListener('mousedown', this.#onThumbMouseDown.bind(this, 'min'));
    this.maxThumb.addEventListener('mousedown', this.#onThumbMouseDown.bind(this, 'max'));
    this.sliderTrack.addEventListener('click', this.#onTrackClick.bind(this));

    // Touch events for mobile support
    this.minThumb.addEventListener('touchstart', this.#onThumbTouchStart.bind(this, 'min'), { passive: false });
    this.maxThumb.addEventListener('touchstart', this.#onThumbTouchStart.bind(this, 'max'), { passive: false });
  }

  /**
   * Handle thumb touch start (for mobile)
   */
  #onThumbTouchStart = (thumb, event) => {
    event.preventDefault();
    this.activeThumb = thumb;
    this.isDragging = true;

    const touch = event.touches[0];
    this.#onTouchMove({ clientX: touch.clientX });

    document.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    document.addEventListener('touchend', this.#onTouchEnd);
  };

  /**
   * Handle touch move during drag
   */
  #onTouchMove = (event) => {
    if (!this.isDragging) return;

    const clientX = event.clientX || event.touches?.[0]?.clientX;
    if (!clientX) return;

    const rect = this.sliderTrack.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    // Ensure value is valid
    if (isNaN(value) || value < this.minPrice || value > this.maxPrice) {
      return;
    }

    if (this.activeThumb === 'min') {
      const maxValue = this.#getThumbValue('max');
      if (value >= maxValue - 1) return;
      this.#setThumbValue('min', value);
    } else {
      const minValue = this.#getThumbValue('min');
      if (value <= minValue + 1) return;
      this.#setThumbValue('max', value);
    }

    this.#updateSliderRange();
    this.#updateInputsFromSlider();
    this.#updateSliderLabels();

    if (event.preventDefault) {
      event.preventDefault();
    }
  };

  /**
   * Handle touch end
   */
  #onTouchEnd = () => {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    const wasDragging = this.activeThumb !== null;
    this.activeThumb = null;
    
    document.removeEventListener('touchmove', this.#onTouchMove);
    document.removeEventListener('touchend', this.#onTouchEnd);

    // Update filters after drag ends (but only if we were actually dragging)
    if (wasDragging) {
      // Small delay to ensure slider updates are complete
      requestAnimationFrame(() => {
        this.updatePriceFilterAndResults();
      });
    }
  };

  /**
   * Remove slider event listeners
   */
  #removeSliderListeners() {
    if (this.minThumb) {
      this.minThumb.removeEventListener('mousedown', this.#onThumbMouseDown.bind(this, 'min'));
      this.minThumb.removeEventListener('touchstart', this.#onThumbTouchStart.bind(this, 'min'));
    }
    if (this.maxThumb) {
      this.maxThumb.removeEventListener('mousedown', this.#onThumbMouseDown.bind(this, 'max'));
      this.maxThumb.removeEventListener('touchstart', this.#onThumbTouchStart.bind(this, 'max'));
    }
    if (this.sliderTrack) {
      this.sliderTrack.removeEventListener('click', this.#onTrackClick.bind(this));
    }
    document.removeEventListener('touchmove', this.#onTouchMove);
    document.removeEventListener('touchend', this.#onTouchEnd);
  }

  /**
   * Handle thumb mouse down
   */
  #onThumbMouseDown = (thumb, event) => {
    event.preventDefault();
    this.activeThumb = thumb;
    this.isDragging = true;

    document.addEventListener('mousemove', this.#onMouseMove);
    document.addEventListener('mouseup', this.#onMouseUp);
  };

  /**
   * Handle mouse move during drag
   */
  #onMouseMove = (event) => {
    if (!this.isDragging || !this.activeThumb) return;

    const rect = this.sliderTrack.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Calculate value based on percentage and current range
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    // Ensure value is valid
    if (isNaN(value) || value < this.minPrice || value > this.maxPrice) {
      console.warn('[Price Slider] Invalid value calculated:', value);
      return;
    }

    if (this.activeThumb === 'min') {
      const maxValue = this.#getThumbValue('max');
      if (value >= maxValue - 1) {
        return; // Ensure at least 1 unit gap
      }
      this.#setThumbValue('min', value);
    } else {
      const minValue = this.#getThumbValue('min');
      if (value <= minValue + 1) {
        return; // Ensure at least 1 unit gap
      }
      this.#setThumbValue('max', value);
    }

    this.#updateSliderRange();
    this.#updateInputsFromSlider();
    this.#updateSliderLabels();
  };

  /**
   * Handle mouse up
   */
  #onMouseUp = () => {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    const wasDragging = this.activeThumb !== null;
    this.activeThumb = null;
    
    document.removeEventListener('mousemove', this.#onMouseMove);
    document.removeEventListener('mouseup', this.#onMouseUp);

    // Update filters after drag ends (but only if we were actually dragging)
    if (wasDragging) {
      // Small delay to ensure slider updates are complete
      requestAnimationFrame(() => {
        this.updatePriceFilterAndResults();
      });
    }
  };

  /**
   * Handle track click
   */
  #onTrackClick = (event) => {
    const rect = this.sliderTrack.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const value = Math.round(this.minPrice + (percentage / 100) * this.range);

    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    const distanceToMin = Math.abs(value - minValue);
    const distanceToMax = Math.abs(value - maxValue);

    if (distanceToMin < distanceToMax) {
      // Click closer to min thumb - update min
      const newMin = Math.min(value, maxValue - 1);
      this.#setThumbValue('min', newMin);
    } else {
      // Click closer to max thumb - update max
      const newMax = Math.max(value, minValue + 1);
      this.#setThumbValue('max', newMax);
    }

    this.#updateSliderRange();
    this.#updateInputsFromSlider();
    this.#updateSliderLabels();
    this.updatePriceFilterAndResults();
  };

  /**
   * Handle input change
   * Since inputs are read-only, this should not be triggered by user input
   * But we keep it for programmatic updates
   */
  #onInputChange = (inputType) => {
    // Since inputs are read-only, just sync slider if values changed programmatically
    this.#updateSliderFromInputs();
    this.#updateSliderRange();
    this.#updateSliderLabels();
  };

  /**
   * Get thumb value
   */
  #getThumbValue(thumb) {
    // Safety check - ensure thumb elements exist
    if (!this.minThumb || !this.maxThumb) {
      console.warn('[Price Slider] getThumbValue: thumb elements not found');
      return 0;
    }

    const minThumbData = this.minThumb.dataset?.value ? Number(this.minThumb.dataset.value.replace(/,/g, '')) : null;
    const maxThumbData = this.maxThumb.dataset?.value ? Number(this.maxThumb.dataset.value.replace(/,/g, '')) : null;
    
    let value;
    if (thumb === 'min') {
      value = this.currentMinValue || minThumbData || 0;
    } else {
      // Don't use maxPrice as fallback - it's the maximum range, not the current value
      // Use actual current filter value or 0, not the max possible range
      value = this.currentMaxValue || maxThumbData || 0;
    }
    
    return value;
  }

  /**
   * Set thumb value
   */
  #setThumbValue(thumb, value) {
    const thumbElement = thumb === 'min' ? this.minThumb : this.maxThumb;

    // Safety check - ensure elements exist
    if (!thumbElement) {
      console.warn('[Price Slider] setThumbValue: thumbElement not found for', thumb);
      return;
    }

    // Safety check - ensure range is valid
    if (!this.range || this.range <= 0 || isNaN(this.range)) {
      console.warn('[Price Slider] setThumbValue: Invalid range', this.range);
      return;
    }

    // Safety check - ensure minPrice and maxPrice are valid
    if (isNaN(this.minPrice) || isNaN(this.maxPrice)) {
      console.warn('[Price Slider] setThumbValue: Invalid minPrice or maxPrice', { minPrice: this.minPrice, maxPrice: this.maxPrice });
      return;
    }

    // Safety check - ensure value is a valid number
    if (isNaN(value) || value === null || value === undefined) {
      console.warn('[Price Slider] setThumbValue: Invalid value', value);
      return;
    }

    // Get current values to enforce constraints
    const currentMin = this.#getThumbValue('min');
    const currentMax = this.#getThumbValue('max');

    // Enforce minimum gap and prevent crossing
    if (thumb === 'min') {
      // Min thumb cannot exceed max thumb minus 1 unit
      const maxAllowed = currentMax - 1;
      value = Math.min(value, maxAllowed);
    } else {
      // Max thumb cannot go below min thumb plus 1 unit
      const minAllowed = currentMin + 1;
      value = Math.max(value, minAllowed);
    }

    // Ensure value is within bounds
    value = Math.max(this.minPrice, Math.min(this.maxPrice, value));

    // Safety check before division
    if (this.range <= 0) {
      console.warn('[Price Slider] setThumbValue: Cannot calculate percentage, range is', this.range);
      return;
    }

    const percentage = ((value - this.minPrice) / this.range) * 100;
    const finalPercentage = Math.max(0, Math.min(100, percentage));

    thumbElement.style.left = `${finalPercentage}%`;
    thumbElement.dataset.value = value;

    // Update stored values
    if (thumb === 'min') {
      this.currentMinValue = value;
    } else {
      this.currentMaxValue = value;
    }
  }

  /**
   * Validate and fix slider constraints
   */
  #validateSliderConstraints() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');

    // If min is greater than or equal to max, fix it
    if (minValue >= maxValue) {
      // Reset to safe values
      const safeMin = Math.max(this.minPrice, maxValue - 1);
      const safeMax = Math.min(this.maxPrice, minValue + 1);

      this.#setThumbValue('min', safeMin);
      this.#setThumbValue('max', safeMax);

      console.warn('Slider constraints violated, reset to safe values:', {
        originalMin: minValue,
        originalMax: maxValue,
        newMin: safeMin,
        newMax: safeMax
      });
    }
  }

  /**
   * Update slider range
   */
  #updateSliderRange() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    const minPercentage = ((minValue - this.minPrice) / this.range) * 100;
    const maxPercentage = ((maxValue - this.minPrice) / this.range) * 100;

    this.sliderRange.style.left = `${minPercentage}%`;
    this.sliderRange.style.width = `${maxPercentage - minPercentage}%`;
  }

  /**
   * Update slider from input values
   */
  #updateSliderFromInputs() {
    const minValue = Number(this.minInput.value) || this.minPrice;
    const maxValue = Number(this.maxInput.value) || this.maxPrice;

    // Ensure minimum gap between thumbs
    const safeMin = Math.max(this.minPrice, Math.min(maxValue - 1, minValue));
    const safeMax = Math.min(this.maxPrice, Math.max(minValue + 1, maxValue));

    this.#setThumbValue('min', safeMin);
    this.#setThumbValue('max', safeMax);
  }

  /**
   * Only submit price parameters when the slider is not at its default bounds.
   * Disabled inputs are excluded from FormData, preventing the price filter from being "auto-applied".
   */
  #syncPriceInputsForSubmission() {
    const { minInput, maxInput } = this.refs;
    if (!minInput || !maxInput) return;

    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');

    const minBase = this.minPrice ?? 0;
    const maxBase = this.maxPrice ?? maxValue;

    const isMinDefault = minValue <= minBase;
    const isMaxDefault = maxValue >= maxBase;

    if (isMinDefault) {
      // At default minimum: clear the value but keep the input editable
      minInput.value = '';
    } else {
      minInput.value = String(minValue);
    }

    if (isMaxDefault) {
      // At default maximum: clear the value but keep the input editable
      maxInput.value = '';
    } else {
      maxInput.value = String(maxValue);
    }
  }

  /**
   * Update inputs from slider values
   * Both inputs are read-only and controlled by the slider
   */
  #updateInputsFromSlider() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');
    
    // Only submit when range is narrowed; keep defaults empty/disabled so price isn't implicitly applied.
    this.#syncPriceInputsForSubmission();
  }

  /**
   * Update slider labels
   */
  #updateSliderLabels() {
    const minValue = this.#getThumbValue('min');
    const maxValue = this.#getThumbValue('max');

    if (this.minLabel) {
      this.minLabel.textContent = this.#formatMoney(minValue);
    }
    if (this.maxLabel) {
      this.maxLabel.textContent = this.#formatMoney(maxValue);
    }
  }

  /**
   * Format money value
   */
  #formatMoney(value) {
    // Use Shopify's money formatting
    const currency = window.Shopify?.currency?.active || 'INR';
    const symbol = window.Shopify?.currency?.symbol || '₹';
    return `${symbol}${value.toLocaleString()}`;
  }

  /**
   * Handle filter updates to preserve slider state
   */
  #onFilterUpdated() {
    // Get current slider values
    const currentMin = this.#getThumbValue('min');
    const currentMax = this.#getThumbValue('max');

    // Keep inputs empty/disabled when at defaults so price isn't implicitly applied after any filter update
    this.#syncPriceInputsForSubmission();

    // Update labels
    this.#updateSliderLabels();
  }

  /**
   * Handle re-initialization events
   */
  #onReinitializeSlider() {
    this.#reinitializeSlider();
  }

  /**
   * Observe for DOM elements to be added
   */
  #observeForElements() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if slider elements are now available
          if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
            this.#initializeSlider();
            this.observer.disconnect();
            this.observer = null;
          }
        }
      });
    });

    // Start observing the component for changes
    this.observer.observe(this, {
      childList: true,
      subtree: true
    });

    // Fallback timeout
    setTimeout(() => {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
        // Try to initialize anyway
        if (this.refs.sliderTrack && this.refs.minThumb && this.refs.maxThumb) {
          this.#initializeSlider();
        }
      }
    }, 1000);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}


// Listen for facet updates to re-initialize price sliders
document.addEventListener('DOMContentLoaded', () => {
  // Listen for facet form updates
  const facetsForm = document.querySelector('facets-form-component');
  if (facetsForm) {
    facetsForm.addEventListener('filter-updated', () => {
      // Find all price facet components and re-initialize them
      const priceFacets = document.querySelectorAll('price-facet-component');
      priceFacets.forEach(facet => {
        if (facet.sliderInitialized) {
          // Dispatch a custom event to trigger re-initialization
          facet.dispatchEvent(new CustomEvent('reinitialize-slider'));
        }
      });
    });
  }
});

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    let facetsForm = form
      ? document.getElementById(form)
      : this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (facetsForm instanceof HTMLFormElement) {
      facetsForm = facetsForm.closest('facets-form-component');
    }

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  // Refs are required but may be in conditional, so make them optional and add null checks
  requiredRefs = [];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!listbox || !(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!details || !(details instanceof HTMLDetailsElement) || !summary || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details && details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    // Only disable if using select on mobile (shouldDisable is true)
    // On desktop, we use radio buttons which don't need to be disabled
    if (shouldDisable && isMobile) {
      const inputs = this.querySelectorAll('.sorting-drawer__input[name="sort_by"]');
      inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.disabled = true;
      });
    } else if (shouldDisable && !isMobile) {
      // Desktop: disable select element if it exists
      const selectElement = this.querySelector('select[name="sort_by"]');
      if (selectElement instanceof HTMLSelectElement) {
        selectElement.disabled = true;
      }
    }

    // Close sorting drawer if open (on mobile)
    if (isMobile && event.target instanceof Element) {
      const drawerComponent = event.target.closest('.sorting-drawer-component');
      if (drawerComponent) {
        const dialog = drawerComponent.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && dialog.open) {
          // Close the dialog by calling close() directly or via dialog component
          const dialogComponentEl = drawerComponent.querySelector('dialog-component');
          if (dialogComponentEl) {
            // Use the component's closeDialog method if available
            const closeMethod = /** @type {any} */ (dialogComponentEl).closeDialog;
            if (typeof closeMethod === 'function') {
              closeMethod.call(dialogComponentEl);
            } else {
              dialog.close();
            }
          } else {
            dialog.close();
          }
        }
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (details && details instanceof HTMLDetailsElement) {
      details.open = false;
    } else {
      const localDetails = this.querySelector('details');
      if (localDetails instanceof HTMLDetailsElement) {
        localDetails.open = false;
      }
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
